import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloudDownloadIcon from "@mui/icons-material/CloudDownloadTwoTone";
import SendIcon from "@mui/icons-material/Send";
import { LoadingButton } from "@mui/lab";
import axios from "axios";

import {
  BankSettingsFields,
  ContactPersonDetails,
  GoodsFields,
  GroupFields,
  PartiesFields,
  accountFields,
  crediNoteGeneral,
  debitNoteGeneral,
  debitNoteItemWiseEnteryTable,
  debitNoteNonItemWiseEnteryTable,
  journalEntries,
  partiesDeliveryFields,
  partiesShippingFields,
  paymentVoucher,
  purchaseFields,
  purchaseGeneral,
  purchaseItemWiseEnteryTable,
  purchaseNonItemWiseEnteryTable,
  purchaseRecipt,
  purchaseTcs,
  receiptVoucher,
  saleDispatch,
  saleGeneral,
  salesFields,
  salesItemWiseEnteryTable,
  salesNonItemWiseEnteryTable,
  salesRecipt,
  salesTcs,
  supplieExtraFeild,
} from "../../utils/module_fields_data";
import TemplateGenerator from "../../components/ImportTemp/TemplateGenerator";
import ImportSummary from "../../components/ImportTemp/ImportSummary";
import {
  showErrorSnackbar,
  showWarningSnackbar,
} from "../../components/snackBar/snakbar";
import { uploadToServer } from "../../library/upload";
import { useLambdaFunctionUrl } from "../../hooks/useEmailSettingLambda";
import { axiosInstanceForLambda } from "../../library/Axios";
import API_ENDPOINTS from "../../apiEndpoints";
import { Link, useLocation } from "react-router-dom";

const MODULE_OPTIONS = [
  "Accounts",
  "Groups",
  "Item",
  "Parties-Customer",
  "Parties-Supplier",
  "Sales",
  "Purchase",
  "Debit Note",
  "Credit Note",
  "Payment Voucher",
  "Receipt Voucher",
  "Journal Entries",
];

const INITIAL_STATE = { isActive: false, modal: false, date: "" };

const MODULE_FIELDS_MAPPING = {
  Item: GoodsFields,
  "Parties-Customer": [...PartiesFields, ...partiesDeliveryFields],
  "Parties-Supplier": [
    ...PartiesFields,
    ...supplieExtraFeild,
    ...partiesShippingFields,
    ...ContactPersonDetails,
    ...BankSettingsFields,
  ],
  Accounts: accountFields,
  Groups: GroupFields,
  Sales: [
    ...saleGeneral,
    ...salesItemWiseEnteryTable,
    ...salesNonItemWiseEnteryTable,
    ...salesTcs,
    ...salesRecipt,
    ...saleDispatch,
  ],
  Purchase: [
    ...purchaseGeneral,
    ...purchaseItemWiseEnteryTable,
    ...purchaseNonItemWiseEnteryTable,
    ...purchaseTcs,
    ...purchaseRecipt,
  ],
  "Debit Note": [
    ...debitNoteGeneral,
    ...debitNoteItemWiseEnteryTable,
    ...debitNoteNonItemWiseEnteryTable,
    ...purchaseTcs,
    // ...salesRecipt,
    ...saleDispatch,
  ],
  "Credit Note": [
    ...crediNoteGeneral,
    ...debitNoteItemWiseEnteryTable,
    ...debitNoteNonItemWiseEnteryTable,
    ...purchaseTcs,
    // ...salesRecipt,
    ...saleDispatch,
  ],
  "Payment Voucher": [...paymentVoucher],
  "Receipt Voucher": [...receiptVoucher],
  "Journal Entries": [...journalEntries],
};

function ImportNew() {
  const [selectModule, setSelectModule] = useState(null);
  const [fields, setFields] = useState([]);
  const [format, setFormat] = useState("xlsx");
  const [file, setFile] = useState(null);
  const [summaryDetails, setSummaryDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summaryView, setSummaryView] = useState(false);
  const [isOpeningBalance, setIsOpeningBalance] = useState(INITIAL_STATE);
  const { callLambdaFunctionUrl, error } = useLambdaFunctionUrl();
  const location = useLocation();
  const {fromLink} = location.state;

  useEffect(() => {
    setFields(MODULE_FIELDS_MAPPING[selectModule] || []);
  }, [selectModule]);

  const handleReset = () => {
    setSelectModule(null);
    setFields([]);
    setSummaryDetails([]);
    setSummaryView(false);
    setIsOpeningBalance(INITIAL_STATE);
    setFile(null);
  };

  const handleDrop = (event) => {
    setFile(event.dataTransfer.files[0]);
  };

  const handleClose = (value) => {
    setIsOpeningBalance((prev) => ({ ...prev, modal: value }));
  };

  const handleBrowseClick = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept =
      ".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    input.addEventListener("change", (event) => {
      const selectedFile = event.target.files[0];
      if (selectedFile) {
        const validTypes = [
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          "text/csv",
        ];
        if (validTypes.includes(selectedFile.type)) {
          setFile(selectedFile);
        } else {
          setFile(null);
          showWarningSnackbar("Please select a valid CSV or XLSX file.");
        }
      }
    });
    input.click();
  };

  const handleProcessBtn = async () => {
    if (!selectModule || !file) {
      showWarningSnackbar("Please select a module and upload a file.");
      const moduleFocus = document.getElementById("combo-box-demo")
      if (moduleFocus) {
        moduleFocus.focus();
      }
      return;
    }
    setLoading(true);
    try {
      const Key = `excel-csv-bucket/${file.name}`;
      await uploadToServer(file, Key, format);

      const moduleName = selectModule.split("-")[1] || selectModule;
      const response = await axiosInstanceForLambda.post(
        API_ENDPOINTS.IMPORT_EXCEL_FROM_S3_URL,
        { moduleType: moduleName, file_key: file.name, format, fields }
      );

      if (response?.status === 200) {
        const {
          invalidCount = 0,
          totalCount = 0,
          invalidRows = [],
          duplicateRows = [],
          balanceAdded,
        } = response.data;
        setSummaryDetails({
          invalidCount,
          totalCount,
          data: [...invalidRows, ...duplicateRows],
        });
        setSummaryView(true);
        if (balanceAdded) {
          setIsOpeningBalance((prev) => ({
            ...prev,
            isActive: true,
            modal: invalidCount === 0,
          }));
        }
      }
    } catch (error) {
      showErrorSnackbar(error?.response?.data?.message || error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ padding: "20px 30px", boxSizing: "content-box" }}>
      <Box
        sx={{
          paddingTop: "20px",
          paddingLeft: { xs: "8px", sm: "70px", lg: "80px" },
          paddingRight: { xs: "8px", lg: "30px" },
          paddingBottom: "8px",
          boxSizing: "content-box",
        }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => e.preventDefault()}
      >
        <Paper
          sx={{
            minHeight: "550px",
            borderRadius: "10px",
            padding: "20px",
            boxShadow:
              "rgba(27, 31, 35, 0.04) 0px 1px 0px, rgba(255, 255, 255, 0.25) 0px 1px 0px inset",
          }}
        >
          {summaryView === false ? (
            <>
              <Box>
                <Autocomplete
                  disablePortal
                  id="combo-box-demo"
                  options={MODULE_OPTIONS}
                  size="small"
                  sx={{ maxWidth: 400 }}
                  onChange={(event, newValue) => {
                    setSelectModule(newValue);
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Module" />
                  )}
                />
              </Box>
              <Box sx={{ marginTop: 2, marginBottom: 2 }}>
                <FormControl variant="standard">
                  <FormLabel id="demo-row-radio-buttons-group-label">
                    Format 
                  </FormLabel>
                  <RadioGroup
                    row
                    aria-labelledby="demo-row-radio-buttons-group-label"
                    name="row-radio-buttons-group"
                    value={format}
                    onChange={(e) => setFormat(e.target.value)}
                  >
                    <FormControlLabel
                      value="xlsx"
                      control={<Radio />}
                      label="xlsx"
                    />
                    <FormControlLabel
                      value="csv"
                      control={<Radio />}
                      label="csv"
                    />
                  </RadioGroup>
                  {/* <FormHelperText>{formatError}</FormHelperText> */}
                </FormControl>
              </Box>
              <TemplateGenerator
                module={selectModule}
                fields={fields}
                format={format}
              />
              <Box
                sx={{
                  border: "1px dashed #ccc",
                  borderRadius: "5px",
                  paddingTop: "14px",
                  paddingBottom: { xs: "14px", md: "20px" },
                  textAlign: "center",
                  marginBottom: "16px",
                  width: { xs: "100%", md: "80%", lg: "50%" },
                }}
                onDrop={handleDrop}
              >
                <Box>
                  <CloudDownloadIcon
                    sx={{ fontSize: "68px", marginBottom: "16px" }}
                  />
                </Box>
                <Box>
                  {file ? (
                    <Typography variant="body2">{file.name}</Typography>
                  ) : (
                    <Typography variant="body2">
                      Drag and drop to import file
                    </Typography>
                  )}

                  <Box sx={{display:"flex",flexDirection:{xs:"column",sm:"row"},justifyContent:"center",alignItems:"center",gap:{xs:"10px",sm:"30px"}}}>
                    <Button
                      variant="outlined"
                      sx={{height:"35px",marginLeft:{xs:"0%",sm:"25%"},marginTop:{xs:"20px",sm:"0px"} }}
                      onClick={handleBrowseClick}
                      startIcon={<CloudDownloadIcon />}
                    >
                      Browse File
                    </Button>

                    <Stack
                      direction={{xs:"column",sm:"row"}}
                      spacing={3}
                      sx={{ marginTop: 2, marginBottom: 2 }}
                    >
                      <Button variant="outlined" onClick={() => setFile(null)}  disabled={!file}>
                        Cancel
                      </Button>
                      <LoadingButton
                        loading={loading}
                        disabled={!file}
                        onClick={handleProcessBtn}
                        loadingPosition="start"
                        variant="contained"
                        startIcon={<SendIcon />}
                      >
                        Proceed
                      </LoadingButton>
                    </Stack>
                  </Box>
                </Box>
              </Box>
              {/* <Stack
                direction="row"
                spacing={3}
                sx={{ marginTop: 2, marginBottom: 2 }}
              >
                <Button variant="outlined" onClick={() => setFile(null)}>
                  Cancel
                </Button>
                <LoadingButton
                  loading={loading}
                  disabled={!file}
                  onClick={handleProcessBtn}
                  loadingPosition="start"
                  variant="contained"
                  startIcon={<SendIcon />}
                >
                  Proceed
                </LoadingButton>
              </Stack> */}
              <Box sx={{display:"flex", justifyContent:"end"}}>
                <Link to = {fromLink}>             
              <Button  variant="contained">BACK</Button>
              </Link>
              </Box>
            </>
          ) : (
            <ImportSummary
              summaryDetails={summaryDetails}
              setSummaryDetails={setSummaryDetails}
              module={selectModule}
              file={file}
              setSummaryView={setSummaryView}
              isOpenModal={isOpeningBalance}
              onClose={handleClose}
              onReset={handleReset}
              onConfirm={(date) =>
                setIsOpeningBalance((prev) => ({
                  ...prev,
                  date: date,
                  isActive: false,
                }))
              }
            />
          )}
        </Paper>
      </Box>
    </Box>
  );
}

export default ImportNew;
